import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomModal from "src/components/configuracion/components/customModal";
import ScfoButton from "src/components/shared/ScfoButton";
import { formatMoneyWithCurrency } from "src/utils/formatter";
import { importAlertState } from "src/redux/actions/alert";
import { importUpdateTransactions } from "src/redux/actions/conciliacion";
import {
  CreateDocumentReport,
  DismissPossibleDuplicates,
  ReportDocumentType,
  ReportReason,
} from "src/graphql/documentReports";
import { dt } from "src/utils/documentsI18n";
import "src/components/documentostributarios/ModalDeleteObligations.scss";
import "./PossibleDuplicateReviewModal.scss";

function hasTransactionMatches(data) {
  return (Array.isArray(data?.matchs) ? data.matchs : []).some(
    (match) => match?.docId != null && String(match.docId).trim() !== ""
  );
}

function hasTransactionEntries(data) {
  const entriesData = data?.entries || data?.entryStatus;
  return !!(
    entriesData?.completed === true ||
    Number(entriesData?.amount || 0) > 0 ||
    (Array.isArray(entriesData?.entryList) && entriesData.entryList.length > 0)
  );
}

function stopRowEvents(e) {
  if (!e) return;
  e.stopPropagation();
}

function peerId(peer) {
  return String(peer?.id || peer?._id || "");
}

function buildPeerList(data, transactionId) {
  const fromApi = Array.isArray(data?.possibleDuplicatePeers)
    ? data.possibleDuplicatePeers.filter((peer) => peerId(peer))
    : [];
  if (fromApi.length >= 2) return fromApi;

  const self = {
    id: transactionId,
    stringDate: data?.stringDate,
    amount: data?.amount,
    description: data?.description,
    accountNumber: data?.accountNumber,
    currency: data?.currency,
    type: data?.type,
  };
  const others = fromApi.filter((peer) => peerId(peer) !== String(transactionId));
  return [self, ...others];
}

/**
 * Clickable "Posible duplicado" badge. Select an option, then Aplicar.
 * Clicks must not bubble to the transaction row (that remounts and drops modal state).
 */
export default function PossibleDuplicateReviewControls({ data }) {
  const dispatch = useDispatch();
  const { selectedCompany } = useSelector((state) => state.companyList);
  const [open, setOpen] = useState(false);
  const [choice, setChoice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cleared, setCleared] = useState(false);
  const { executeMutation: dismissDuplicates } = DismissPossibleDuplicates();
  const { executeMutation: createReport } = CreateDocumentReport();

  const transactionId = data?.id || data?._id;
  const companyId = selectedCompany?.id || data?.companyId;

  const hasMatches = hasTransactionMatches(data);
  const hasAsientos = hasTransactionEntries(data);
  const reportBlocked = hasMatches || hasAsientos;
  const blockedText =
    hasMatches && hasAsientos
      ? dt("report.blockedBodyBoth")
      : hasAsientos
        ? dt("report.blockedBodyAsientos")
        : dt("report.blockedBody");

  if (!transactionId || !data?.possibleDuplicate || cleared) return null;

  const peers = buildPeerList(data, transactionId);

  const refresh = () => {
    setTimeout(() => dispatch(importUpdateTransactions(true)), 400);
  };

  const close = () => {
    if (loading) return;
    setOpen(false);
    setChoice(null);
    setError(null);
  };

  const openModal = (e) => {
    stopRowEvents(e);
    if (loading) return;
    setChoice(null);
    setError(null);
    setOpen(true);
  };

  const selectChoice = (e, next) => {
    stopRowEvents(e);
    if (loading) return;
    if (next === "report" && reportBlocked) return;
    setChoice(next);
  };

  const onApply = async (e) => {
    stopRowEvents(e);
    if (!choice) return;
    if (choice === "report" && reportBlocked) return;

    setLoading(true);
    setError(null);
    try {
      if (choice === "dismiss") {
        const result = await dismissDuplicates({ companyId, transactionId });
        if (result.error) throw new Error(result.error.message);
        setCleared(true);
        setOpen(false);
        setChoice(null);
        dispatch(
          importAlertState({
            type: "success",
            isOpen: true,
            message: "Los movimientos se mantienen y dejan de marcarse como posibles duplicados.",
          })
        );
        refresh();
        return;
      }

      const result = await createReport({
        input: {
          companyId,
          documentId: transactionId,
          documentType: ReportDocumentType.TRANSACTION,
          reason: ReportReason.DUPLICATED,
        },
      });
      if (result.error) throw new Error(result.error.message);
      setCleared(true);
      setOpen(false);
      setChoice(null);
      dispatch(
        importAlertState({
          type: "success",
          isOpen: true,
          message: "El movimiento se reportó como duplicado.",
        })
      );
      refresh();
    } catch (err) {
      setError(err.message || "No se pudo completar la acción.");
    } finally {
      setLoading(false);
    }
  };

  const applyDisabled =
    loading ||
    !choice ||
    (choice === "report" && reportBlocked);

  return (
    <>
      <button
        type="button"
        className="possible-duplicate-badge-btn"
        title="Revisar posible duplicado"
        onClick={openModal}
        onMouseDown={stopRowEvents}
      >
        Posible duplicado
      </button>

      <CustomModal
        open={open}
        onClose={close}
        title="Posible duplicado"
        size="large"
        dimmer="blurring"
        classNameModal="possible-duplicate-modal"
        classNameContent="possible-duplicate-modal-content"
        modalStyle={{
          width: "1200px",
          maxWidth: "95%",
          minHeight: "72vh",
          maxHeight: "85vh",
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
        closeOnDimmerClick={false}
        closeOnEscape={false}
        onClick={stopRowEvents}
        onMouseDown={stopRowEvents}
        customActions={
          <div
            className="tw-flex tw-justify-end tw-items-center tw-gap-2 tw-w-full"
            onClick={stopRowEvents}
            onMouseDown={stopRowEvents}
          >
            <ScfoButton
              type="button"
              variant="ghost"
              onClick={(e) => {
                stopRowEvents(e);
                close();
              }}
              disabled={loading}
            >
              Cancelar
            </ScfoButton>
            <ScfoButton
              type="button"
              variant="danger"
              onClick={onApply}
              loading={loading}
              disabled={applyDisabled}
            >
              Aplicar
            </ScfoButton>
          </div>
        }
        content={
          <div
            className="possible-duplicate-modal-body"
            onClick={stopRowEvents}
            onMouseDown={stopRowEvents}
          >
            <section className="possible-duplicate-col possible-duplicate-col-peers">
              <div className="possible-duplicate-col-title">
                Movimientos en cuestión
                <span className="possible-duplicate-peers-count">{peers.length}</span>
              </div>
              <ul className="possible-duplicate-peers-list">
                {peers.map((peer) => {
                  const id = peerId(peer);
                  const isCurrent = id === String(transactionId);
                  const peerAmount = formatMoneyWithCurrency(
                    peer.amount,
                    peer.currency || data.currency || "CLP"
                  );
                  return (
                    <li
                      key={id || peerAmount}
                      className={`possible-duplicate-peer${
                        isCurrent ? " is-current" : ""
                      }`}
                    >
                      <div className="possible-duplicate-peer-top">
                        <span className="possible-duplicate-peer-desc">
                          {peer.description || "Movimiento"}
                        </span>
                        {isCurrent ? (
                          <span className="possible-duplicate-peer-tag">Actual</span>
                        ) : null}
                      </div>
                      <div className="possible-duplicate-peer-meta">
                        <span>{peer.stringDate || "—"}</span>
                        <span className="possible-duplicate-peer-amount">{peerAmount}</span>
                      </div>
                      {peer.accountNumber ? (
                        <div className="possible-duplicate-peer-account">
                          Cta. {peer.accountNumber}
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </section>

            <section className="possible-duplicate-col possible-duplicate-col-choices">
              <h4 className="possible-duplicate-col-title">Opciones</h4>
              <p className="possible-duplicate-lead">¿Este movimiento está duplicado?</p>

            <div className="possible-duplicate-choices" role="radiogroup" aria-label="Opción de duplicado">
              <button
                type="button"
                role="radio"
                aria-checked={choice === "dismiss"}
                className={`possible-duplicate-choice possible-duplicate-choice--keep${
                  choice === "dismiss" ? " is-selected" : ""
                }`}
                onClick={(e) => selectChoice(e, "dismiss")}
                onMouseDown={stopRowEvents}
                disabled={loading}
              >
                <span className="possible-duplicate-choice-radio" aria-hidden />
                <span className="possible-duplicate-choice-text">
                  <span className="possible-duplicate-choice-title">No, mantener ambos</span>
                  <span className="possible-duplicate-choice-desc">
                    Los dos son válidos. Dejan de marcarse como posibles duplicados.
                  </span>
                </span>
              </button>

              <button
                type="button"
                role="radio"
                aria-checked={choice === "report"}
                className={`possible-duplicate-choice possible-duplicate-choice--report${
                  choice === "report" ? " is-selected" : ""
                }`}
                onClick={(e) => selectChoice(e, "report")}
                onMouseDown={stopRowEvents}
                disabled={loading || reportBlocked}
              >
                <span className="possible-duplicate-choice-radio" aria-hidden />
                <span className="possible-duplicate-choice-text">
                  <span className="possible-duplicate-choice-title">Sí, reportar</span>
                  <span className="possible-duplicate-choice-desc">
                    Esta es la copia de más. Se reporta y se oculta.
                  </span>
                </span>
              </button>
            </div>

            {reportBlocked ? (
              <p className="delete-obligation-modal-warning">{blockedText}</p>
            ) : null}
            {error ? <p className="delete-obligation-modal-warning">{error}</p> : null}
            </section>
          </div>
        }
      />
    </>
  );
}

export function PossibleDuplicateReviewButton({ data }) {
  return <PossibleDuplicateReviewControls data={data} />;
}
